/**
 * Modifié par : Landy
 * Date : 13/02/2026
 * 
 * Modifications :
 * - Intégration des nouveaux indicateurs Backend : Top 5 Missionnaires et Top 5 Thématiques.
 * - Ajout de nouvelles colonnes dans le tableau détaillé : "Nom et Prénoms" et "Durée (jours)".
 * - Mise en place d'une fenêtre modale interactive pour afficher le détail des missions d'un agent au clic.
 * - Amélioration de la mise en page (Colonnes côte à côte pour les indicateurs).
 * - Correction de la gestion des filtres Direction et Service pour une réinitialisation propre.
 * 
 * Objectif : Rendre le tableau de bord plus interactif, visuellement riche (Thématiques, Top agents) 
 * et faciliter la vérification des données grâce au tableau détaillé et à la fenêtre modale.
 */
import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useNavigate, useLocation } from "react-router-dom";
import MainContainer from "../../../components/MainContainer";
import { Grid, Button, Card, CardContent, Typography, Box, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@material-ui/core";
import { headerData } from '../../../data/modules/dashboard-header';
import { LoadingButton } from '@mui/lab';
import SearchIcon from '@mui/icons-material/Search';
import PlanningMissionService from "../../../services/PlanningMissionService";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import LocalisationMisService from "../../../services/LocalisationMisService";
import { Chip } from "@material-ui/core";
import { BarChart } from '@mui/x-charts/BarChart';
import MissionService from "../../../services/MissionService";
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import LoadingTexteBackdrop from "../../tools/LoadingTexteBackdrop";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import Title from "../../../components/Title";
import Spacer from '../../../components/Spacer';
import Message from "../../tools/Message";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EventNoteIcon from '@mui/icons-material/EventNote';
import MapIcon from '@mui/icons-material/Map';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TableViewIcon from '@mui/icons-material/TableView';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const useStyles = makeStyles((theme) => ({
  seeMore: {
    marginTop: theme.spacing(3),
  },
  root: {
    padding: theme.spacing(0.5, 0.5, 0),
  },
  kpiCard: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  kpiValue: {
    fontWeight: 'bold',
    fontSize: '1.5rem',
    color: theme.palette.primary.main,
  },
  filterContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
}));



export default function StatistiquePlanning() {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsloading] = useState(false);
  const [direction, setDirection] = useState([]);
  const [service, setService] = useState([]);
  const [region, setRegion] = useState([]);
  const [district, setDistrict] = useState([]);
  const [commune, setCommune] = useState([]);
  const [rows, setRows] = useState([]);
  const [stat, setStat] = useState([]);
  const [kpis, setKpis] = useState({
    total: 0,
    avgDuration: 0,
    totalRegions: 0,
    top3Regions: [],
    topMissionnaires: [],
    topThematiques: [],
    topMissionnaireByRegion: {}
  });
  const [tickPlacement, setTickPlacement] = useState('middle');
  const [tickLabelPlacement, setTickLabelPlacement] = useState('middle');
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
  const [flattenedData, setFlattenedData] = useState([]);
  const [uniqueType, setUniqueType] = useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [selectedMissionnaire, setSelectedMissionnaire] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openThemeModal, setOpenThemeModal] = useState(false);
  const [openRegionModal, setOpenRegionModal] = useState(false);
  const [rawDetails, setRawDetails] = useState([]);
  const [filter, setFilter] = useState({
    date_debut: null,
    date_fin: null,
    directiondest: null,
    directionmiss: null,
    servicemiss: null,
    region: {},
    district: {},
    commune: {},
    group: { value: "directionmiss", label: "Direction de missionnaire" },
    pivot: null
  });
  const [appliedPivot, setAppliedPivot] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const grouppivot = [
    { value: "directionmiss", label: "Direction de missionnaire" },
    { value: "servicemiss", label: "Service de missionnaire" }
  ];

  useEffect(() => {
    // Restauration de l'état si on revient de la carte via le state de navigation
    if (location.state && location.state.mapResultsState) {
      const { rows, stat, kpis, rawDetails, flattenedData, uniqueType, appliedPivot } = location.state.mapResultsState;
      setRows(rows || []);
      setStat(stat || []);
      setKpis(kpis || {
        total: 0,
        avgDuration: 0,
        totalRegions: 0,
        top3Regions: [],
        topMissionnaires: [],
        topThematiques: [],
        topMissionnaireByRegion: {}
      });
      setRawDetails(rawDetails || []);
      setFlattenedData(flattenedData || []);
      setUniqueType(uniqueType || []);
      setAppliedPivot(appliedPivot);
      setHasSearched(true);

      if (location.state.mapFilterState) {
        setFilter(location.state.mapFilterState);
      }
    }
  }, [location]);

  const columns = [
    { id: 'nom', label: 'Nom et Prénoms', minWidth: 170 },
    { id: 'directionpers', label: 'Direction du missionnaire', minWidth: 140 },
    { id: 'service_code', label: 'Service du missionnaire', minWidth: 140 },
    { id: 'reference', label: 'Réference mission', minWidth: 100 },
    { id: 'mission_title', label: 'Thématique (Objet de missions)', minWidth: 200 },
    { id: 'date_debut', label: 'Date de départ', minWidth: 150 },
    { id: 'date_fin', label: 'Date de retour', minWidth: 140 },
    { id: 'code', label: 'Direction dest.', minWidth: 100 },
    { id: 'nom_region', label: 'Région dest.', minWidth: 120 },
    { id: 'nom_district', label: 'District dest.', minWidth: 120 },
    { id: 'duree', label: 'Durée', minWidth: 60 },
  ];

  function handleChange(event) {
    const { name, value } = event.target;
    setFilter({ ...filter, [name]: value });
  }

  function handleChangeDirection(event, value) {
    let newGroup = filter.group;
    let newPivot = filter.pivot;

    if (value) {
      // Si une direction est sélectionnée, on affiche auto "Service de missionnaire" dans la case Groupé par
      // On utilise la référence de grouppivot[1] pour que l'Autocomplete le détecte bien
      newGroup = { value: "directionmiss", label: "Direction de missionnaire" };
      newPivot = grouppivot[1];
    } else {
      newGroup = { value: "directionmiss", label: "Direction de missionnaire" };
      newPivot = null;
    }

    setFilter({ ...filter, directionmiss: value, servicemiss: null, group: newGroup, pivot: newPivot });

    if (value && value.direction_id) {
      PlanningMissionService.getServiceappui(value.direction_id).then((result) => {
        if (result && result.data) {
          setService(result.data);
        }
      }).catch((error) => {
        console.error('Erreur lors de l\'appel API:', error);
      });
    } else {
      setService([]);
    }
  }

  function handleChangeDirectiondest(event, value) {
    setFilter({ ...filter, directiondest: value });
  }

  function handleChangeService(event, value) {
    setFilter({ ...filter, servicemiss: value });
  }

  const [message, setMessage] = React.useState({
    open: false,
    color: "green",
    messageInfo: "",
  });
  const { open, color, messageInfo } = message;

  function handleChangeRegion(event, value) {

    setFilter({ ...filter, region: value });
    if (value && value.id_region) {
      LocalisationMisService.getDistrictByRegion(value.id_region).then((result) => {
        if (result && result.data) {
          setDistrict(result.data);
        }
      });
    }
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleOpenDetails = (missionnaire) => {
    setSelectedMissionnaire(missionnaire);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedMissionnaire(null);
  };

  const handleOpenThemeModal = (theme) => {
    setSelectedTheme(theme);
    setOpenThemeModal(true);
  };

  const handleOpenRegionModal = () => {
    if (kpis.top3Regions && kpis.top3Regions.length > 0) {
      setOpenRegionModal(true);
    }
  };

  const handleOpenMap = () => {
    // On privilégie les données détaillées (une ligne par mission et par missionnaire)
    // pour que la carte puisse reproduire exactement la logique backend.
    const dataForMap = rawDetails.length > 0 ? rawDetails : rows;

    if (dataForMap.length > 0) {
      // On passe les données via l'état de navigation pour éviter la limite de quota du localStorage (5Mo)
      navigate("/mission/map", {
        state: {
          mapMissionData: dataForMap,
          mapMissionTopByRegion: kpis.topMissionnaireByRegion || {},
          mapFilterState: filter,
          mapResultsState: {
            rows,
            stat,
            kpis,
            rawDetails,
            flattenedData,
            uniqueType,
            appliedPivot
          }
        }
      });
    } else {
      setMessage({
        ...message,
        open: true,
        color: 'red',
        messageInfo: "Aucune donnée à afficher sur la carte. Veuillez d'abord appliquer un filtre."
      });
    }
  };

  const handleCloseThemeModal = () => {
    setOpenThemeModal(false);
    setSelectedTheme(null);
  };

  const handleCloseRegionModal = () => {
    setOpenRegionModal(false);
  };

  const handleExportPDF = async () => {
    if (rows.length === 0) {
      setMessage({
        ...message,
        open: true,
        color: 'red',
        messageInfo: "Aucune donnee a exporter. Veuillez d'abord appliquer un filtre."
      });
      return;
    }

    setOpenLoadingSimpleBackdrop(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPosition = 20;

      // 1. Ajouter le logo
      try {
        const logoImg = await fetch(process.env.PUBLIC_URL + '/assets/images/logo.png');
        const logoBlob = await logoImg.blob();
        const logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(logoBlob);
        });
        pdf.addImage(logoBase64, 'PNG', 15, yPosition, 50, 15);
        yPosition += 20;
      } catch (error) {
        console.warn('Logo non charge pour le PDF');
      }

      // 2. Titre
      pdf.setFontSize(18);
      pdf.setTextColor(53, 133, 205);
      pdf.setFont('helvetica', 'bold');
      pdf.text('SUIVI MISSIONS EFFECTUEES', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // 3. Date d'export
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.setFont('helvetica', 'normal');
      const exportDate = new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
      pdf.text(`Date d'export : ${exportDate}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // 4. Filtres appliques - TOUS LES FILTRES
      pdf.setFontSize(14);
      pdf.setTextColor(53, 133, 205);
      pdf.setFont('helvetica', 'bold');
      pdf.text('FILTRES APPLIQUES', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;

      // Dessiner un cadre pour les filtres
      const filterBoxY = yPosition - 4;
      pdf.setDrawColor(220, 220, 220);
      pdf.setLineWidth(0.3);

      pdf.setFontSize(9);
      pdf.setTextColor(60, 60, 60);

      // Configuration des colonnes pour un alignement parfait (Style Gauche -> Droite)
      const leftLabelX = 20;
      const leftColonX = 64;  // Décalé à droite pour laisser de la place aux noms complets
      const leftValueX = 67;

      const rightLabelX = 110;
      const rightColonX = 148;
      const rightValueX = 151;

      const lineHeight = 7;
      let currentY = yPosition;

      // Valeurs par défaut
      const nonDefini = 'Non défini';

      // Colonne gauche
      const dateDepart = filter.date_debut || nonDefini;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Date départ', leftLabelX, currentY);
      pdf.text(':', leftColonX, currentY);
      pdf.setFont('helvetica', 'normal');
      pdf.text(String(dateDepart), leftValueX, currentY);

      currentY += lineHeight;
      const dateRetour = filter.date_fin || nonDefini;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Date retour', leftLabelX, currentY);
      pdf.text(':', leftColonX, currentY);
      pdf.setFont('helvetica', 'normal');
      pdf.text(String(dateRetour), leftValueX, currentY);

      currentY += lineHeight;
      const directionMiss = filter.directionmiss ? `${filter.directionmiss.name} (${filter.directionmiss.code})` : nonDefini;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Direction missionnaires', leftLabelX, currentY);
      pdf.text(':', leftColonX, currentY);
      pdf.setFont('helvetica', 'normal');
      const dirMissText = pdf.splitTextToSize(directionMiss, 40);
      pdf.text(dirMissText, leftValueX, currentY);

      currentY += lineHeight * dirMissText.length;
      const serviceMiss = filter.servicemiss ? `${filter.servicemiss.name} (${filter.servicemiss.code})` : nonDefini;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Service missionnaires', leftLabelX, currentY);
      pdf.text(':', leftColonX, currentY);
      pdf.setFont('helvetica', 'normal');
      const servMissText = pdf.splitTextToSize(serviceMiss, 40);
      pdf.text(servMissText, leftValueX, currentY);

      const leftColMaxY = currentY + lineHeight;

      // Colonne droite
      currentY = yPosition;
      const regionDest = filter.region && filter.region.nom_region ? filter.region.nom_region : nonDefini;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Région destination', rightLabelX, currentY);
      pdf.text(':', rightColonX, currentY);
      pdf.setFont('helvetica', 'normal');
      pdf.text(String(regionDest), rightValueX, currentY);

      currentY += lineHeight;
      const directionDest = filter.directiondest ? `${filter.directiondest.name} (${filter.directiondest.code})` : nonDefini;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Direction destination', rightLabelX, currentY);
      pdf.text(':', rightColonX, currentY);
      pdf.setFont('helvetica', 'normal');
      const dirDestText = pdf.splitTextToSize(directionDest, 40);
      pdf.text(dirDestText, rightValueX, currentY);

      currentY += lineHeight * dirDestText.length;
      const groupePar = filter.pivot ? filter.pivot.label : nonDefini;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Groupé par', rightLabelX, currentY);
      pdf.text(':', rightColonX, currentY);
      pdf.setFont('helvetica', 'normal');
      pdf.text(String(groupePar), rightValueX, currentY);

      const rightColMaxY = currentY + lineHeight;
      const maxY = Math.max(leftColMaxY, rightColMaxY);

      // Cadre autour des filtres avec un peu plus de padding
      const filterBoxHeight = maxY - filterBoxY;
      pdf.rect(15, filterBoxY, pageWidth - 30, filterBoxHeight);
      yPosition = maxY + 12;

      // 5. KPIs
      pdf.setFontSize(12);
      pdf.setTextColor(53, 133, 205);
      pdf.setFont('helvetica', 'bold');
      pdf.text('INDICATEURS CLES', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 8;

      const kpiBoxWidth = (pageWidth - 40) / 3;
      const kpiBoxHeight = 25;
      const kpiData = [
        { label: 'Missions Filtrées', value: kpis.total },
        { label: 'Durée Moyenne (jours)', value: kpis.avgDuration },
        { label: 'Régions visitées', value: kpis.totalRegions }
      ];

      kpiData.forEach((kpi, index) => {
        const xPos = 15 + (index * (kpiBoxWidth + 5));
        pdf.setFillColor(53, 133, 205);
        pdf.rect(xPos, yPosition, kpiBoxWidth, kpiBoxHeight, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(kpi.label, xPos + kpiBoxWidth / 2, yPosition + 8, { align: 'center' });
        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.text(String(kpi.value), xPos + kpiBoxWidth / 2, yPosition + 18, { align: 'center' });
      });
      yPosition += kpiBoxHeight + 12;

      // 6. Top 5 Missionnaires
      if (kpis.topMissionnaires && kpis.topMissionnaires.length > 0) {
        pdf.setFontSize(12);
        pdf.setTextColor(53, 133, 205);
        pdf.setFont('helvetica', 'bold');
        pdf.text('TOP 5 MISSIONNAIRES', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 8;

        pdf.setFontSize(9);
        pdf.setTextColor(60, 60, 60);
        pdf.setFont('helvetica', 'normal');
        kpis.topMissionnaires.forEach((m, index) => {
          const text = `${index + 1}. ${m.direction} - ${m.name}`;
          const count = `${m.count} missions`;
          pdf.text(text, 20, yPosition);
          pdf.setFont('helvetica', 'bold');
          pdf.text(count, pageWidth - 25, yPosition, { align: 'right' });
          pdf.setFont('helvetica', 'normal');
          yPosition += 6;
        });
        yPosition += 6;
      }

      // 7. Top 5 Thematiques
      if (kpis.topThematiques && kpis.topThematiques.length > 0) {
        pdf.setFontSize(12);
        pdf.setTextColor(53, 133, 205);
        pdf.setFont('helvetica', 'bold');
        pdf.text('TOP 5 THEMATIQUE (OBJET DE MISSIONS)', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 8;

        pdf.setFontSize(9);
        pdf.setTextColor(60, 60, 60);
        pdf.setFont('helvetica', 'normal');
        kpis.topThematiques.forEach((t, index) => {
          const maxLength = 60;
          let title = t.title.toLowerCase();
          title = title.charAt(0).toUpperCase() + title.slice(1);
          if (title.length > maxLength) {
            title = title.substring(0, maxLength) + '...';
          }
          const text = `${index + 1}. ${title}`;
          const count = `${t.count} fois`;
          pdf.text(text, 20, yPosition);
          pdf.setFont('helvetica', 'bold');
          pdf.text(count, pageWidth - 25, yPosition, { align: 'right' });
          pdf.setFont('helvetica', 'normal');
          yPosition += 6;
        });
      }

      // 8. Capture du graphique
      pdf.addPage();
      yPosition = 20;
      pdf.setFontSize(14);
      pdf.setTextColor(53, 133, 205);
      pdf.setFont('helvetica', 'bold');
      pdf.text('GRAPHIQUES', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;

      try {
        const chartElements = document.querySelectorAll('.MuiChartsAxis-root');
        if (chartElements.length > 0) {
          const chartContainer = chartElements[0].closest('svg').parentElement;
          const canvas = await html2canvas(chartContainer, {
            scale: 2,
            backgroundColor: '#ffffff'
          });
          const chartImage = canvas.toDataURL('image/png');
          const chartWidth = pageWidth - 30;
          const chartHeight = (canvas.height * chartWidth) / canvas.width;

          if (yPosition + chartHeight > pageHeight - 20) {
            pdf.addPage();
            yPosition = 20;
          }

          pdf.addImage(chartImage, 'PNG', 15, yPosition, chartWidth, chartHeight);
        }
      } catch (error) {
        console.warn('Impossible de capturer le graphique:', error);
        pdf.setFontSize(10);
        pdf.setTextColor(150, 150, 150);
        pdf.text('Graphique non disponible', pageWidth / 2, yPosition, { align: 'center' });
      }

      // Pagination
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Document PGI - Page ${i}/${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      }

      // Sauvegarde
      const fileName = `suivi_missions_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      alert('Une erreur est survenue lors de l\'export PDF.');
    } finally {
      setOpenLoadingSimpleBackdrop(false);
    }
  };

  const handleExportExcel = async () => {
    if (rows.length === 0) {
      setMessage({
        ...message,
        open: true,
        color: 'red',
        messageInfo: "Aucune donnée à exporter. Veuillez d'abord appliquer un filtre."
      });
      return;
    }

    setOpenLoadingSimpleBackdrop(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Missions Filtrées');

      // 1. Ajout du Logo
      try {
        const response = await fetch(process.env.PUBLIC_URL + '/assets/images/logo.png');
        const imageBuffer = await response.arrayBuffer();
        const logoId = workbook.addImage({
          buffer: imageBuffer,
          extension: 'png',
        });
        // Positionnement précis du logo (tl = top-left)
        worksheet.addImage(logoId, {
          tl: { col: 0.2, row: 0.2 },
          ext: { width: 220, height: 60 }
        });
      } catch (imgError) {
        console.warn('Logo non trouvé ou impossible à charger pour l\'export Excel');
      }

      // 2. Ajout du Titre
      worksheet.mergeCells('A4:F4');
      const titleCell = worksheet.getCell('A4');
      titleCell.value = 'SUIVI MISSIONS EFFECTUEES';
      titleCell.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FF3585CD' } };
      titleCell.alignment = { vertical: 'middle', horizontal: 'center' };

      // 2.1 Ajout des filtres (dynamique)
      let currentRow = 6;
      worksheet.getCell(`A${currentRow}`).value = 'Filtres appliqués :';
      worksheet.getCell(`A${currentRow}`).font = { bold: true, italic: true };
      currentRow++;

      if (filter.date_debut || filter.date_fin) {
        worksheet.getRow(currentRow).values = ['Période :', `${filter.date_debut || '?'} au ${filter.date_fin || '?'}`];
        currentRow++;
      }
      if (filter.directionmiss) {
        worksheet.getRow(currentRow).values = ['Dir. Missionnaire :', filter.directionmiss.code];
        currentRow++;
      }
      if (filter.servicemiss) {
        worksheet.getRow(currentRow).values = ['Serv. Missionnaire :', filter.servicemiss.code || filter.servicemiss.label];
        currentRow++;
      }
      if (filter.region && filter.region.nom_region) {
        worksheet.getRow(currentRow).values = ['Région :', filter.region.nom_region];
        currentRow++;
      }
      if (filter.directiondest) {
        worksheet.getRow(currentRow).values = ['Dir. Destination :', filter.directiondest.code];
        currentRow++;
      }

      currentRow++; // Une ligne d'espace avant le tableau

      // 3. Définition des colonnes (pour la largeur uniquement ici)
      worksheet.columns = columns.map(col => ({
        key: col.id,
        width: col.minWidth / 5
      }));

      // 4. En-tête du tableau
      const headerRow = worksheet.getRow(currentRow);
      headerRow.values = columns.map(col => col.label);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF3585CD' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
      headerRow.height = 30;
      currentRow++;

      // 5. Ajout des données
      rows.forEach((row, index) => {
        const dataRow = worksheet.addRow(row);
        dataRow.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.alignment = { vertical: 'middle', wrapText: true };
        });
      });

      // Exportation
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const fileName = `suivi_missions_effectuees_${new Date().toISOString().split('T')[0]}.xlsx`;

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

    } catch (error) {
      console.error('Erreur lors de l\'export Excel:', error);
      alert('Une erreur est survenue lors de l\'export.');
    } finally {
      setOpenLoadingSimpleBackdrop(false);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  function handleChangeDistrict(event, value) {
    setFilter({ ...filter, district: value });
    if (value && value.id_district) {
      LocalisationMisService.getCommune(value.id_district).then((result) => {
        if (result && result.data) {
          setCommune(result.data);
        }
      });
    }
  }

  function handleFilter() {
    if (filter.group && filter.group.value === "district" && Object.keys(filter.region).length <= 0) {
      setMessage({
        ...message,
        open: true,
        color: 'red',
        messageInfo: "Choisissez une région pour déterminer le district de destination et afficher la valeur correcte."
      });
    }
    else {
      // Réinitialisation des données avant le nouvel appel pour éviter les données fantômes
      setStat([]);
      setFlattenedData([]);
      setRows([]);
      setRawDetails([]);
      setKpis({
        total: 0,
        avgDuration: 0,
        totalRegions: 0,
        top3Regions: [],
        topMissionnaires: [],
        topThematiques: [],
        topMissionnaireByRegion: {}
      });

      setHasSearched(true);
      setOpenLoadingSimpleBackdrop(true);
      MissionService.detailgraphe(
        filter.date_debut,
        filter.date_fin,
        filter.directionmiss,
        filter.servicemiss,
        filter.directiondest,
        filter.region,
        filter.group,
        filter.pivot
      ).then((result) => {

        if (result && result.status !== false) {
          if (!result.stat) result.stat = []; // Sécurité : évite le crash si pas de stats
          let statData = [];
          const groupValue = filter.group.value;


          if (groupValue === "directionmiss" || groupValue === "direction_dest") {
            if (direction && direction.length > 0) {
              statData = direction.map((item) => ({
                label: item.code || "N/A",
                value: 0,
                detail: []
              }));
            } else if (result.stat) {
              // Fallback : Utiliser les labels du backend si la liste locale est vide
              statData = result.stat.map((item) => ({
                label: item.direction || "N/A",
                value: 0,
                detail: []
              }));
            }
          } else if (groupValue === "servicemiss") {
            if (service && service.length > 0) {
              statData = service.map((item) => ({
                label: item.code || item.label || "N/A",
                value: 0,
                detail: []
              }));
            } else if (result.stat) {
              statData = result.stat.map((item) => ({
                label: item.direction || "N/A",
                value: 0,
                detail: []
              }));
            }
          } else if (groupValue === "district") {
            if (district && district.length > 0) {
              statData = district.map((item) => ({
                label: item.nom_district || "N/A",
                value: 0,
                detail: []
              }));
            } else if (result.stat) {
              statData = result.stat.map((item) => ({
                label: item.direction || "N/A",
                value: 0,
                detail: []
              }));
            }
          } else if (groupValue === "region") {
            if (region && region.length > 0) {
              statData = region.map((item) => ({
                label: item.nom_region || "N/A",
                value: 0,
                detail: []
              }));
            } else if (result.stat) {
              statData = result.stat.map((item) => ({
                label: item.direction || "N/A",
                value: 0,
                detail: []
              }));
            }
          }



          if (statData.length > 0 && result.stat) {
            const updatedDataset = statData.map(element => {
              // Normalisation pour une correspondance robuste (Trim + Uppercase)
              const matchingElement = result.stat.find(elements =>
                (elements.direction || "").toString().trim().toUpperCase() ===
                (element.label || "").toString().trim().toUpperCase()
              );

              return {
                ...element,
                value: matchingElement ? Number(matchingElement.missions) : element.value,
                detail: matchingElement ? matchingElement.details : element.detail
              };
            });

            // Si le filtrage renvoie des éléments du backend qui n'étaient pas dans notre statData initial
            // (ex: une direction "Non défini" ou un code inconnu), on les ajoute au début
            if (result.stat.length > 0) {
              result.stat.forEach(resItem => {
                const alreadyExists = updatedDataset.some(upd =>
                  (upd.label || "").toString().trim().toUpperCase() ===
                  (resItem.direction || "").toString().trim().toUpperCase()
                );
                if (!alreadyExists && Number(resItem.missions) > 0) {
                  updatedDataset.unshift({
                    label: resItem.direction,
                    value: Number(resItem.missions),
                    detail: resItem.details || []
                  });
                }
              });
            }

            setStat(updatedDataset);


            if (filter.pivot !== null) {
              const flattened = updatedDataset.flatMap(item =>
                item.detail.map(details => ({
                  label: item.label,
                  type: details.direction,
                  value: details.missions
                }))
              );
              const transformedData = [];

              // Récupérer toutes les régions uniques
              const uniqueRegions = [...new Set(flattened.map(item => item.label))];
              const uniqueTypes = [...new Set(flattened.map(item => item.type))];

              setUniqueType(uniqueTypes);
              // Créer un objet par région
              uniqueRegions.forEach(region => {
                const regionObj = { region };

                // Ajouter chaque type comme propriété
                uniqueTypes.forEach(type => {
                  const item = flattened.find(d => d.label === region && d.type === type);
                  regionObj[type] = item ? item.value : 0;
                });

                transformedData.push(regionObj);
              });
              setFlattenedData(transformedData);

            }
          }
          setAppliedPivot(filter.pivot);
          setRows(result.data);
          // Sauvegarde des détails bruts renvoyés par l'API (une ligne par mission et par utilisateur)
          setRawDetails(result.details || []);

          // Récupération des indicateurs (KPIs) calculés par le Backend
          if (result.kpis) {
            setKpis({
              total: result.kpis.total,
              avgDuration: result.kpis.avgDuration,
              totalRegions: result.kpis.totalRegions || 0,
              top3Regions: result.kpis.top3Regions || [],
              topMissionnaires: result.kpis.topMissionnaires || [], // UTILISE LE TOP 5 DU BACKEND
              topThematiques: result.kpis.topThematiques || [],
              topMissionnaireByRegion: result.kpis.topMissionnaireByRegion || {}
            });
          }

          setOpenLoadingSimpleBackdrop(false);

        } else {
          console.error("Erreur ou données manquantes dans la réponse de l'API.");
          setOpenLoadingSimpleBackdrop(false);

          // Réinitialisation explicite en cas d'erreur de réponse
          setRows([]);
          setStat([]);
          setKpis({
            total: 0,
            avgDuration: 0,
            totalRegions: 0,
            top3Regions: [],
            topMissionnaires: [],
            topThematiques: [],
            topMissionnaireByRegion: {}
          });

          setMessage({
            ...message,
            open: true,
            color: 'red',
            messageInfo: result && result.message ? result.message : "Aucune information disponible pour ce filtre."
          });
        }
      }).catch((error) => {
        console.error("Erreur lors de l'appel API detailgraphe:", error);
        setOpenLoadingSimpleBackdrop(false);

        // Réinitialisation en cas d'erreur critique
        setRows([]);
        setStat([]);
        setKpis({
          total: 0,
          avgDuration: 0,
          totalRegions: 0,
          top3Regions: [],
          topMissionnaires: [],
          topThematiques: [],
          topMissionnaireByRegion: {}
        });

        setMessage({
          ...message,
          open: true,
          color: 'red',
          messageInfo: "Erreur lors de la récupération des données."
        });
      });
    }
  }


  const chartSetting = {
    yAxis: [
      {
        label: '',
      },
    ],
    series: [
      {
        dataKey: 'value',
        label: 'Nombre de missions',
        valueFormatter: (value) => `${value} `,
        color: '#3585CD',
      },
    ],
    height: 600,
    sx: {
      [`& .${axisClasses.directionY} .${axisClasses.label}`]: {
        transform: 'translateX(-5px)',
      },
    },
  };

  const chartSettingpivot = {
    yAxis: [
      {
        label: 'Nombre de missions',
      },
    ],
    height: 600,
    sx: {
      [`& .${axisClasses.directionY} .${axisClasses.label}`]: {
        transform: 'translateX(-5px)',
      },
    },
  };

  const typeColors = {
    'DG': '#3585CD',
    'DIRA': '#4CAF50',
    'DIRB': '#FF9800',
    'DIRT': '#9C27B0',
    'DIRP': '#F44336',
    'ARH': '#053252ff',
    'ARM': '#eba09aff',
    'ARS': '#422b46ff',
    'DIRK': '#a7906eff',
    'DIRF': '#80af82ff',
    'DIRU': '#5a2d2aff'
  };

  const extractThematique = (title) => {
    if (!title || typeof title !== 'string') return 'non spécifié';

    let words = title.toUpperCase().split(' ');

    // 1. Suppression des dates et périodes
    const months = ['JANVIER', 'FEVRIER', 'MARS', 'AVRIL', 'MAI', 'JUIN', 'JUILLET', 'AOUT', 'SEPTEMBRE', 'OCTOBRE', 'NOVEMBRE', 'DECEMBRE'];
    words = words.filter(w => !months.includes(w));

    // 2. Nettoyage du titre
    title = words.join(' ');
    title = title.replace(/\b\d+[eè]r?[eè]me?\b|\b\d+[eè]r?\b/gi, '');
    title = title.replace(/\b\d+\b/g, '');
    title = title.replace(/\s+/g, ' ').trim();

    // 2.5. Suppression des directions en début de titre
    const directionsToSkip = ['DG', 'DIRA', 'DIRB', 'DIRT', 'DIRP', 'DIRK', 'DIRF', 'DIRU', 'ARH', 'ARM', 'ARS', 'DRB', 'DRT', 'DRP', 'DRK', 'DRF', 'DRU', 'DR'];
    words = title.split(' ');

    // Si le premier mot est une direction, on le saute
    if (words.length > 0 && directionsToSkip.includes(words[0])) {
      words.shift(); // On retire la direction du début
      title = words.join(' '); // On reconstruit le titre sans la direction
    }

    // 3. Logique par Verbe d'Action
    words = title.split(' ');
    const actionVerbs = ['SUPERVISION', 'FORMATION', 'MISSION', 'COLLECTE', 'EVALUATION', 'APPUI', 'MISE', 'REUNION', 'ATELIER', 'PRISE', 'SUIVI', 'SENSIBILISATION', 'ACCOMPAGNEMENT', 'CONTROLE', 'VERIFICATION', 'ANALYSE', 'MONITORING', 'ENQUETE', 'RECOUVREMENT', 'VISITE', 'AUDIT', 'INSPECTION', 'COMMUNICATION', 'RAPPORT', 'PROJET'];
    const stopWords = ['DU', 'DES', 'AUX', 'LES', 'SUR', 'DE', 'LE', 'LA', 'D\'', 'POUR', 'AVEC', 'DANS', 'AU', 'PAR', 'DERNIERS', 'DERNIERE', 'DERNIERES', 'EN', 'A', 'À', 'UN', 'UNE'];
    const timeWords = ['JOUR', 'JOURS', 'MOIS', 'AN', 'ANS', 'ANNEE', 'ANNEES', 'SEMAINE', 'SEMAINES'];

    const thematiquesExtraites = [];
    let currentSegment = [];

    for (let i = 0; i < words.length; i++) {
      const w = words[i];
      if (stopWords.includes(w)) continue;

      if (actionVerbs.includes(w)) {
        if (currentSegment.length > 0) {
          thematiquesExtraites.push(currentSegment.join(' '));
          currentSegment = [];
        }

        const segment = [w];
        let nextWordsCount = 0;
        let j = i + 1;
        for (; j < words.length && nextWordsCount < 2; j++) {
          const nextWord = words[j];
          const nextWordNorm = nextWord.replace('À', 'A');
          const isLinkWord = nextWordNorm === 'EN' || nextWordNorm === 'A';
          const lastWord = segment[segment.length - 1];
          if (isLinkWord && (lastWord === 'MISE' || lastWord === 'PRISE')) {
            segment.push(nextWord);
            continue;
          }
          const isJour = nextWordNorm === 'JOUR' || nextWordNorm === 'JOURS';
          if (isJour && segment.includes('MISE')) {
            segment.push(nextWord);
            nextWordsCount = 2;
            break;
          }
          if (stopWords.includes(nextWord)) continue;
          if (timeWords.includes(nextWordNorm)) continue;
          if (!isNaN(Number(nextWord))) continue;
          segment.push(nextWord);
          nextWordsCount++;
        }
        thematiquesExtraites.push(segment.join(' '));
        i = j - 1;
        continue;
      }

      if (currentSegment.length > 0) {
        currentSegment.push(w);
        if (currentSegment.length >= 3) {
          thematiquesExtraites.push(currentSegment.join(' '));
          currentSegment = [];
        }
      }
    }

    if (currentSegment.length > 0) {
      thematiquesExtraites.push(currentSegment.join(' '));
    }

    // Filtrage 2 mots min
    const finalResults = thematiquesExtraites.filter(t => t.trim().split(' ').length >= 2);

    if (finalResults.length === 0) {
      const filteredWords = words.filter(w => !stopWords.includes(w) && !timeWords.includes(w) && isNaN(Number(w)));
      return (filteredWords.slice(0, 3).join(' ').toLowerCase() || 'non spécifié');
    }

    return finalResults.join(' & ').toLowerCase();
  };

  // Fonction pour calculer le Top 5 à partir des données du tableau
  const calculateTop5FromTable = (tableData) => {
    const missionnaires = {};

    tableData.forEach(row => {
      const nom = row.nom;
      const missionId = row.mission_planning_id;

      if (!missionnaires[nom]) {
        missionnaires[nom] = {
          id: row.user_id || nom,
          display: `${row.directionpers || 'ND'} - ${row.nom}`,
          name: row.nom,
          direction: row.directionpers,
          count: 0,
          allMissions: [],
          seenMissions: new Set()
        };
      }

      if (missionId && !missionnaires[nom].seenMissions.has(missionId)) {
        missionnaires[nom].count++;
        missionnaires[nom].seenMissions.add(missionId);

        missionnaires[nom].allMissions.push({
          reference: row.reference,
          date_debut: row.date_debut,
          date_fin: row.date_fin,
          mission_title: row.mission_title,
          destination_name: row.nom_region || ''
        });
      }
    });

    return Object.values(missionnaires)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };

  useEffect(() => {
    // 1. Charger les listes de base (Directions, Régions)
    PlanningMissionService.getDirection().then((result) => {
      if (result && result.data) {
        setDirection(result.data);
      }
    }).catch((error) => {
      console.error('Error fetching direction data:', error);
    });

    LocalisationMisService.getRegions().then((result) => {
      if (result && result.data) {
        setRegion(result.data);
      }
    });

    // 2. Restaurer l'état (Filtres + Résultats) si on revient de la carte
    const savedFilter = localStorage.getItem('MAP_FILTER_STATE');
    const savedResults = localStorage.getItem('MAP_RESULTS_STATE');
    let parsedFilter = null;

    if (savedFilter) {
      parsedFilter = JSON.parse(savedFilter);
      setFilter(parsedFilter);

      // Restaurer les services si une direction était sélectionnée
      if (parsedFilter.directionmiss && parsedFilter.directionmiss.direction_id) {
        PlanningMissionService.getServiceappui(parsedFilter.directionmiss.direction_id).then((result) => {
          if (result && result.data) {
            setService(result.data);
          }
        });
      }

      // Restaurer les districts si une région était sélectionnée
      if (parsedFilter.region && parsedFilter.region.id_region) {
        LocalisationMisService.getDistrictByRegion(parsedFilter.region.id_region).then((result) => {
          if (result && result.data) {
            setDistrict(result.data);
          }
        });
      }
    }

    if (savedResults) {
      const res = JSON.parse(savedResults);
      setRows(res.rows || []);
      setStat(res.stat || []);
      setKpis(res.kpis || kpis);
      setRawDetails(res.rawDetails || []);
      setFlattenedData(res.flattenedData || []);
      setUniqueType(res.uniqueType || []);
      setAppliedPivot(res.appliedPivot || (parsedFilter ? parsedFilter.pivot : null));
      setHasSearched(true);
    }
  }, []);

  function handleCloseMessage() {
    setMessage({ ...message, open: false });
  }

  return (
    <MainContainer {...headerData}>
      <LoadingTexteBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />
      <Message
        open={open}
        color={color}
        messageInfo={messageInfo}
        handleClose={handleCloseMessage}
      />
      <React.Fragment>
        <Grid container spacing={3} className={classes.root}>
          {/* Filters Section */}
          <Grid item xs={12}>
            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={handleFilter}
                loading={isLoading}
                startIcon={<SearchIcon />}
                style={{ borderRadius: '4px', padding: '8px 24px', fontWeight: 'bold' }}
              >
                FILTRER
              </LoadingButton>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<MapIcon />}
                onClick={handleOpenMap}
                style={{ borderRadius: '4px', padding: '8px 24px', fontWeight: 'bold' }}
              >
                VUE SUR CARTE
              </Button>
            </Box>
            <Paper className={classes.filterContainer} elevation={1}>
              <Grid container spacing={2}>
                {/* Colonne 1 : Dates */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Date départ"
                    size="small"
                    variant="outlined"
                    type="date"
                    value={filter.date_debut}
                    name="date_debut"
                    InputLabelProps={{ shrink: true }}
                    onChange={handleChange}
                    style={{ marginBottom: '24px' }}
                  />
                  <TextField
                    fullWidth
                    label="Date retour"
                    size="small"
                    variant="outlined"
                    type="date"
                    value={filter.date_fin}
                    name="date_fin"
                    InputLabelProps={{ shrink: true }}
                    onChange={handleChange}
                  />
                </Grid>

                {/* Colonne 2 : Directions */}
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    onChange={handleChangeDirection}
                    name="directionmiss"
                    options={direction}
                    value={filter.directionmiss || null}
                    getOptionLabel={(option) => `${option.name} (${option.code})`}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth label="Direction des missionnaires" size="small" variant="outlined" />
                    )}
                    style={{ marginBottom: '24px' }}
                  />
                  <Autocomplete
                    onChange={handleChangeDirectiondest}
                    value={filter.directiondest || null}
                    name="directiondest"
                    options={direction}
                    getOptionLabel={(option) => `${option.name} (${option.code})`}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth label="Direction de destination" size="small" variant="outlined" />
                    )}
                  />
                </Grid>

                {/* Colonne 3 : Destination & Groupement */}
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    onChange={(event, newValue) => handleChangeRegion(event, newValue)}
                    options={region}
                    getOptionLabel={(option) => option?.nom_region || ""}
                    value={filter.region || null}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth label="Region de destination" size="small" variant="outlined" />
                    )}
                    style={{ marginBottom: '24px' }}
                  />
                  <Autocomplete
                    options={grouppivot}
                    getOptionLabel={(option) => option.label}
                    value={filter.pivot}
                    onChange={(event, newValue) => {
                      setFilter({ ...filter, pivot: newValue });
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" variant="outlined" fullWidth label="Groupé par" />
                    )}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>


          {/* Results Sections (Shown only after search) */}
          {hasSearched && (
            <>
              {/* Top 5 Missionaries Section */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} style={{ padding: '20px', height: '100%' }}>
                  <Typography variant="h6" style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                    <TrendingUpIcon color="primary" /> Top 5 Missionnaires
                  </Typography>
                  {kpis.topMissionnaires.length > 0 ? (
                    <Grid container spacing={2}>
                      {kpis.topMissionnaires.map((m, index) => (
                        <Grid item xs={12} key={index}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" p={1} style={{ borderBottom: index < 4 ? '1px solid #eee' : 'none' }}>
                            <Typography variant="body2" style={{ maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <strong>{m.direction}</strong> - {m.name}
                            </Typography>
                            <Chip
                              label={`${m.count} Missions`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              style={{ fontWeight: 'bold', cursor: 'pointer' }}
                              onClick={() => handleOpenDetails(m)}
                            />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography color="textSecondary">
                      Aucune donnée disponible
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Top 5 Thématiques Section */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} style={{ padding: '20px', height: '100%' }}>
                  <Typography variant="h6" style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                    <SearchIcon color="primary" /> Thématique (Objet de missions)
                  </Typography>
                  {kpis.topThematiques.length > 0 ? (
                    <Grid container spacing={2}>
                      {kpis.topThematiques.map((t, index) => (
                        <Grid item xs={12} key={index}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" p={1} style={{ borderBottom: index < 4 ? '1px solid #eee' : 'none' }}>
                            <Typography variant="body2" title={t.title} style={{ maxWidth: '80%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                              <span style={{ textTransform: 'capitalize' }}>{t.title.toLowerCase()}</span>
                            </Typography>
                            <Chip
                              label={`${t.count} fois`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              onClick={() => handleOpenThemeModal(t)}
                              style={{ fontWeight: 'bold', cursor: 'pointer' }}
                            />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography color="textSecondary">
                      Aucune donnée disponible
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* KPI Cards */}
              <Grid item xs={12} md={4}>
                <Paper className={classes.kpiCard} elevation={1}>
                  <Typography variant="subtitle2" gutterBottom>Missions Filtrées</Typography>
                  <Typography className={classes.kpiValue}>{kpis.total}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper className={classes.kpiCard} elevation={1}>
                  <Typography variant="subtitle2" gutterBottom>Durée Moyenne (jours)</Typography>
                  <Typography className={classes.kpiValue}>{kpis.avgDuration}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper
                  className={classes.kpiCard}
                  elevation={1}
                  style={{ textAlign: 'center' }}
                >
                  <Typography variant="subtitle2" gutterBottom>Nombre total de régions visitées</Typography>
                  <Typography className={classes.kpiValue}>{kpis.totalRegions}</Typography>
                </Paper>
              </Grid>
            </>
          )}


          {/* Details Modal */}
          <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                  Activités de : {selectedMissionnaire?.name}
                </Typography>
                <IconButton onClick={handleCloseModal} size="small" style={{ color: 'red' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell style={{ fontWeight: 'bold' }}>Référence</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Date début</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Date fin</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Objet</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Destination</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selectedMissionnaire?.allMissions || []).map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{row.reference}</TableCell>
                        <TableCell>{row.date_debut}</TableCell>
                        <TableCell>{row.date_fin}</TableCell>
                        <TableCell style={{ textTransform: 'capitalize' }}>
                          {row.mission_title.toLowerCase()}
                        </TableCell>
                        <TableCell>{row.destination_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {(!selectedMissionnaire?.allMissions || selectedMissionnaire.allMissions.length === 0) && (
                <Box p={3} textAlign="center">
                  <Typography color="textSecondary">Aucune donnée détaillée disponible.</Typography>
                </Box>
              )}
            </DialogContent>
          </Dialog>

          {/* Theme Detail Modal */}
          <Dialog open={openThemeModal} onClose={handleCloseThemeModal} maxWidth="md" fullWidth>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                  Activités regroupées sous : {selectedTheme?.title}
                </Typography>
                <IconButton onClick={handleCloseThemeModal} size="small" style={{ color: 'red' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '15px' }}>
                Voici la liste des missions ayant été regroupées dans cette catégorie thématique :
              </Typography>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell style={{ fontWeight: 'bold' }}>Missionnaire</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Date début</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Date fin</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Objet complet</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selectedTheme?.missions || []).map((m, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell style={{ textTransform: 'capitalize' }}>
                          {(m.nom || '').toLowerCase()}
                        </TableCell>
                        <TableCell>{m.date_debut}</TableCell>
                        <TableCell>{m.date_fin}</TableCell>
                        <TableCell style={{ textTransform: 'capitalize', fontSize: '0.8rem' }}>
                          {(m.title || '').toLowerCase()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
          </Dialog>



          {/* Charts Section */}
          <Grid item xs={12}>
            <Paper elevation={1} style={{ padding: '20px', minHeight: '100px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              {!hasSearched ? (
                <Box textAlign="center" py={3}>
                  <Typography variant="h6" color="textSecondary" style={{ fontStyle: 'italic' }}>
                    Veuillez effectuer une recherche
                  </Typography>
                </Box>
              ) : (
                <>
                  {rows.length > 0 && (
                    <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
                      <Button
                        variant="contained"
                        onClick={handleExportPDF}
                        startIcon={<PictureAsPdfIcon />}
                        style={{
                          borderRadius: '4px',
                          padding: '8px 24px',
                          fontWeight: 'bold',
                          backgroundColor: '#f44336',
                          color: '#fff'
                        }}
                      >
                        PDF
                      </Button>
                    </Box>
                  )}
                  {appliedPivot === null && stat.length > 0 ? (
                    <BarChart
                      dataset={stat}
                      xAxis={[
                        {
                          scaleType: 'band',
                          dataKey: 'label',
                          tickPlacement,
                          tickLabelPlacement,
                          tickLabelStyle: {
                            angle: 45,
                            textAnchor: 'start',
                            fontSize: 12,
                          },
                        },
                      ]}
                      margin={{ bottom: 100, left: 50, right: 50 }}
                      {...chartSetting}
                    />
                  ) : appliedPivot !== null ? (
                    <BarChart
                      dataset={flattenedData}
                      xAxis={[
                        {
                          scaleType: 'band',
                          dataKey: 'region',
                          tickPlacement: 'middle',
                          tickLabelPlacement: 'middle',
                          tickLabelStyle: {
                            angle: 45,
                            textAnchor: 'start',
                            fontSize: 12,
                          },
                        },
                      ]}
                      series={uniqueType.map(type => ({
                        dataKey: type,
                        label: type,
                        valueFormatter: (value) => `${value}`,
                        color: typeColors[type] || '#3585CD',
                      }))}
                      margin={{ bottom: 100, left: 50, right: 50 }}
                      {...chartSettingpivot}
                      slotProps={{
                        legend: {
                          direction: 'row',
                          position: { vertical: 'top', horizontal: 'middle' },
                          padding: { top: 0, bottom: 20 },
                        },
                      }}
                    />
                  ) : (
                    <Box textAlign="center" py={3}>
                      <Typography color="textSecondary">Aucune donnée disponible pour ce graphique</Typography>
                    </Box>
                  )}
                </>
              )}
            </Paper>
          </Grid>

          {/* Data Table Section */}
          <Grid item xs={12}>
            {rows.length > 0 &&
              <Paper elevation={1} style={{ padding: '20px' }}>
                <Box display="flex" justifyContent="center" alignItems="center" mb={2} position="relative">
                  <Title style={{ textAlign: 'center', flex: 1 }}>Informations détaillées</Title>
                  <Box style={{ position: 'absolute', right: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleExportExcel}
                      startIcon={<TableViewIcon />}
                      style={{
                        borderRadius: '4px',
                        padding: '8px 24px',
                        fontWeight: 'bold',
                        backgroundColor: '#4caf50',
                        color: '#fff'
                      }}
                    >
                      EXCEL
                    </Button>
                  </Box>
                </Box>
                <Spacer y={1} />
                <TableContainer sx={{ maxHeight: 530 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        {columns.map((column) => (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            style={{ minWidth: column.minWidth, fontWeight: 'bold' }}
                          >
                            {column.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((row, index) => {
                          return (
                            <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                              {columns.map((column) => {
                                const value = row[column.id];
                                return (
                                  <TableCell key={column.id} align={column.align}>
                                    {column.id === 'reference' ? (
                                      <Chip label={value} size="small" variant="outlined" color="primary" />
                                    ) : column.id === 'nom' ? (
                                      <Typography variant="body2" style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                                        {value.toLowerCase()}
                                      </Typography>
                                    ) : column.id === 'directionpers' || column.id === 'service_code' ? (
                                      <Typography variant="body2">
                                        {value}
                                      </Typography>
                                    ) : column.id === 'mission_title' ? (
                                      <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                                        {value.toLowerCase().replace(/^( (missions?|directions?)\s+(de\s+|d['’]|des\s+)?)/i, '').trim()}
                                      </Typography>
                                    ) : value}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 100]}
                  component="div"
                  count={rows.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            }
          </Grid>
        </Grid>
        {/* Modal Top 3 Destinations */}
        <Dialog open={openRegionModal} onClose={handleCloseRegionModal} maxWidth="xs" fullWidth>
          <DialogTitle style={{ fontWeight: 'bold', borderBottom: '1px solid #eee' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                <MapIcon color="primary" /> Top 3 Destinations
              </Box>
              <IconButton onClick={handleCloseRegionModal} size="small" style={{ color: 'red' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent style={{ padding: '20px' }}>
            <Typography variant="body2" color="textSecondary" align="center" style={{ marginBottom: '20px' }}>
              Les 3 régions les plus visitées sur cette période.
            </Typography>
            {kpis.top3Regions.map((region, index) => (
              <Box
                key={index}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                p={2}
                style={{ background: '#f9f9f9', borderRadius: '8px', borderLeft: `4px solid ${index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32'}` }}
              >
                <Typography variant="h6" style={{ fontSize: '1rem', fontWeight: 600 }}>{region.name}</Typography>
                <Chip label={`${region.count} visites`} color="primary" size="small" />
              </Box>
            ))}
          </DialogContent>
        </Dialog>
      </React.Fragment>
    </MainContainer>

  );
}
